import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useZipCodeQuery } from '../../api/getZipCodeLookup';
import PolicyZipCode from '../../assets/icons/PolicyZipCode';
import ControlledTextField from '../../components/TextField/ControlledTextField';
import Button from '../../components/styledComponents/Button';
import colors from '../../constants/colors';
import { useDirectAccessAnalytics } from '../../hooks/useAnalytics';
import useUpdateTitle from '../../hooks/useUpdateTitle';
import {
  preventNonNumbers,
  setCookieWithExpiration,
} from '../../utils/helpers';
import ClubRedirectDialog from './Dialogs/ClubRedirectDialog';
import SplitView from './SplitView';
import {
  validationMessages,
  ZipCodeFormValues,
  zipCodeValidationSchema,
} from './DirectAccess/schemas/zipCodeCheck';
import { isAxiosError } from 'axios';

const ZipCodeCheck = () => {
  useUpdateTitle('Enter Zip Code');
  const [searchParams] = useSearchParams();

  const [zipCode, setZipCode] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const { data, isLoading, error } = useZipCodeQuery(zipCode);
  const [showClubRedirectDialog, setShowClubRedirectDialog] = useState(false);
  const [outOfTerritoryZipCode, setOutOfTerritoryZipCode] = useState(false);
  const { track } = useDirectAccessAnalytics();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
    setError,
  } = useForm<ZipCodeFormValues>({
    resolver: yupResolver(zipCodeValidationSchema),
    mode: 'onChange',
  });

  const handleChange =
    (fieldName: string) =>
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    ) =>
      preventNonNumbers(e.target.value, (val: string) => {
        onChange(e);
      });

  useEffect(() => {
    if (data?.clubCode) {
      track('Link Accessed', {
        event_type: 'Link Accessed',
        location: 'Feature Card',
        club_code: data.clubCode,
      });
      if (data?.loginURL && data?.loginType === 'CLUB') {
        setLoginUrl(data.loginURL);
        setShowClubRedirectDialog(true);
        reset();
      } else {
        setLoginUrl('/login');
      }
      setCookieWithExpiration('mpr_zip', JSON.stringify(data), { days: 30 });
      if (searchParams.has('redirectTo')) {
        localStorage.setItem(
          'mypolicyRedirectTo',
          searchParams.get('redirectTo')!
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (isAxiosError(error) && error?.response?.status === 404) {
      if (error?.response?.data?.errorCode === 'LOGIN_TYPE_NOT_FOUND') {
        setOutOfTerritoryZipCode(true);
        setShowClubRedirectDialog(true);
        setLoginUrl(`https://zipgate.aaa.com/?ZipCodeEntry=${zipCode}`);
      }

      if (error?.response?.data?.errorCode === 'GET_CLUBS_BY_ZONE_NOT_FOUND') {
        setError(
          'zipCode',
          { message: validationMessages.length },
          { shouldFocus: true }
        );
      }
    }
  }, [error]);

  if (loginUrl === '/login') {
    return <Navigate to={loginUrl} replace />;
  }

  return (
    <SplitView data-testid="zip-code" Icon={<PolicyZipCode />}>
      <Stack
        direction="column"
        sx={{
          justifyContent: "center",
          px: { mobile: 2, tablet: 14, desktop: 11 }
        }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Policy zip code
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: 1,
            color: colors.csaaDark,
          }}
        >
          This helps us locate your local AAA club and provide you with the best
          experience.
        </Typography>
        <Stack
          direction="column"
          component="form"
          onSubmit={handleSubmit((values) => {
            track('Pre-Registration Submitted', {
              event_type: 'Link Accessed',
              location: 'Feature Card',
            });
            setZipCode(values.zipCode);
          })}
        >
          <ControlledTextField
            name="zipCode"
            control={control}
            label="Policy zip code"
            sx={{ mt: 3 }}
            InputProps={{
              sx: { background: colors.csaaWhite },
              required: true,
            }}
            inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
            handleChange={handleChange('zipCode')}
            data-testid="zip-code-field"
            autoComplete="postal-code"
          />
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            sx={{
              justifyContent: { mobile: 'space-between', tablet: 'flex-end' },
              mt: { mobile: 3, tablet: 5 }
            }}>
            <Button
              type="submit"
              disabled={!isValid}
              loading={zipCode !== '' && isLoading}
              data-testid="zip-code-continue-button"
            >
              Continue
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <ClubRedirectDialog
        open={showClubRedirectDialog}
        closeDialog={() => {
          setShowClubRedirectDialog(false);
          setZipCode('');
        }}
        loginUrl={loginUrl}
        outOfTerritoryZipCode={outOfTerritoryZipCode}
      />
    </SplitView>
  );
};

export default ZipCodeCheck;
